import { useState, useEffect } from 'react';
import Modal from '@mui/material/Modal';
import { MdVisibility as ViewIcon, MdEdit as EditIcon, MdAdminPanelSettings as AdminIcon, MdPerson as PersonIcon, MdGroups as TeamIcon } from 'react-icons/md';
import { IoClose as Close } from 'react-icons/io5';
import ToggleStyleSwitch from '@CommonRepresentationalDisplay/Switch/ToggleStyleSwitch';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import ProjectService from '@Helpers/URLHelpers/ProjectService';
import TeamService from '@Helpers/URLHelpers/TeamService';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import CircularProgress from '@mui/material/CircularProgress';
import ErrorHandler from '@Root/ErrorHandler';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import './ConfirmAddUserForm.scss';

export default function ConfirmAddUserForm(props) {

    const [loading, setLoading] = useState(false);
    const [userOrgPermissions, setUserOrgPermissions] = useState();
    const [isOrgExternalViewer, setIsOrgExternalViewer] = useState(false);
    const [isOrgNotAllowCreate, setIsOrgNotAllowCreate] = useState(false);
    const [readPermission, setReadPermission] = useState(true);
    const [writePermission, setWritePermission] = useState(true);
    const [adminPermission, setAdminPermission] = useState(false);
    const [externalViewerPermission, setExternalViewerPermission] = useState(false);
    const { currentOrg, currentOrgId } = useReduxOrg();
    const { forceUpdateUserListInProject } = useReduxProject();
    const projectService = new ProjectService();
    const teamService = new TeamService();
    const userAuthService = new UserAuthService();
    const errorHandler = new ErrorHandler();
    const className = 'confirmAddUserForm';

    useEffect(() => {
        renderData();
    }, [props])

    const renderData = async() => {
        setLoading(true);
        try {
            if(props.childType === 'user' && !!currentOrg){
                let userPermissions = await renderUserPermissions();
                if(userPermissions.externalViewer){
                    setIsOrgExternalViewer(true);
                    setExternalViewerPermission(true);
                    setReadPermission(false);
                    setWritePermission(false);
                    setAdminPermission(false);
                }
                else if(!userPermissions.allowCreate){
                    setIsOrgNotAllowCreate(true);
                    setExternalViewerPermission(false);
                    setReadPermission(true);
                    setWritePermission(false);
                    setAdminPermission(false);
                }
            }
        } finally {
            setLoading(false);
        }
    }

    const renderUserPermissions = async() => {
        try {
            let userPermissions = await userAuthService.getUserPermissionForParent(props.userId, 'org', currentOrgId);
            userPermissions = JSON.parse(userPermissions.body);
            setUserOrgPermissions(userPermissions);
            return userPermissions;
        } catch (err) {
            errorHandler.handleError(err);
            return {};
        }
    }

    const handleSendInvite = async(e) => {
        e.preventDefault();
        setLoading(true);
        try {
            let permissions = [];
            if(externalViewerPermission) {
                permissions.push('external');
            }
            else if(readPermission) {
                permissions.push('read');
                if(writePermission){
                    permissions.push('write');
                    if(adminPermission){
                        permissions.push('admin');
                    }
                }
            }
            if(!!props.project && Object.keys(props.project).length !== 0) {
                if(props.childType === 'user'){
                    let body = {
                        childType: props.childType,
                        userId: props.userId,
                        projectId: props.project.id,
                        permissions: JSON.stringify(permissions)
                    }
                    let userAdded = await projectService.addContributorToProject(body);
                    if(userAdded.status <= 200 && userAdded.body == 'true') {
                        forceUpdateUserListInProject(body.projectId);
                    }
                }
                if(props.childType === 'team'){
                    let body = {
                        childType: props.childType,
                        teamId: props.teamId,
                        projectId: props.project.id,
                        permissions: JSON.stringify(permissions)
                    }
                    await projectService.addContributorToProject(body);
                }
            }
            if(!!props.team && Object.keys(props.team).length !== 0) {
                let body = {
                    childType: props.childType,
                    userId: props.userId,
                    teamId: props.team.id,
                    permissions: JSON.stringify(permissions)
                }
                await teamService.addContributorToTeam(body);
                props?.addToActiveUsers(props?.userId);
            }
            
            SnackbarManager.show(
                `${props.childType === 'user' ? 'User' : 'Team'} added successfully!`,
                'success'
            );
            
            props.reloadData();
            props.closePopup();
        } catch (err) {
            errorHandler.handleError(err);
            SnackbarManager.show(
                `Failed to add ${props.childType === 'user' ? 'user' : 'team'}. Please try again.`,
                'error'
            );
        } finally {
            setLoading(false);
        }
    }

    const handleReadPermissionsCheck = (e) => {
        let checked = e.target.checked;
        if(checked){
            setReadPermission(true);
        } else {
            setReadPermission(false);
            setWritePermission(false);
            setAdminPermission(false);
        }
    }

    const handleExternalViewerPermissionsCheck = (e) => {
        let checked = e.target.checked;
        if(checked){
            setExternalViewerPermission(true);
            setReadPermission(false);
            setWritePermission(false);
            setAdminPermission(false);
        } else {
            setExternalViewerPermission(false);
        }
    }

    const handleWritePermissionsCheck = (e) => {
        let checked = e.target.checked;
        if(checked && readPermission){
            setWritePermission(true);
        } else {
            setWritePermission(false);
            setAdminPermission(false);
        }
    }

    const handleAdminPermissionsCheck = (e) => {
        let checked = e.target.checked;
        if(checked && readPermission && writePermission){
            setAdminPermission(true);
        } else {
            setAdminPermission(false);
        }
    }

    const displayName = props.childType === 'user' ? props.userFullName : props.teamTitle;
    const parentName = (!!props.project && Object.keys(props.project).length !== 0) 
        ? props.project?.projectName 
        : (!!props.team && Object.keys(props.team).length !== 0) 
        ? props.team?.teamName 
        : '';

    return (
        <Modal
            open={props.open}
            onClose={props.closePopup}>
            <div className={className}>
                <div className={`${className}__container`}>
                    {/* Header */}
                    <div className={`${className}__header`}>
                        <div className={`${className}__header__left`}>
                            <div className={`${className}__header__avatar`}>
                                {props.childType === 'user' ? <PersonIcon /> : <TeamIcon />}
                            </div>
                            <div className={`${className}__header__info`}>
                                <h2 className={`${className}__header__title`}>Add to {parentName}</h2>
                                <p className={`${className}__header__subtitle`}>{displayName}</p>
                            </div>
                        </div>
                        <button 
                            className={`${className}__header__close`} 
                            onClick={props.closePopup}
                            disabled={loading}>
                            <Close />
                        </button>
                    </div>

                    {/* Permissions Section */}
                    <div className={`${className}__permissions`}>
                        <h3 className={`${className}__permissions__title`}>Set Permissions</h3>
                        
                        {/* View Permission */}
                        <div className={`${className}__permission ${externalViewerPermission || isOrgNotAllowCreate ? 'disabled' : ''}`}>
                            <div className={`${className}__permission__icon view`}>
                                <ViewIcon />
                            </div>
                            <div className={`${className}__permission__content`}>
                                <div className={`${className}__permission__header`}>
                                    <div className={`${className}__permission__info`}>
                                        <h4 className={`${className}__permission__name`}>View All</h4>
                                        <p className={`${className}__permission__desc`}>
                                            Can view all items in the {props.parentType}
                                        </p>
                                    </div>
                                    <ToggleStyleSwitch
                                        onChange={handleReadPermissionsCheck}
                                        checked={readPermission}
                                        disabled={externalViewerPermission || isOrgNotAllowCreate || loading}/>
                                </div>
                            </div>
                        </div>

                        {/* Create/Edit Permission */}
                        <div className={`${className}__permission ${(!readPermission || isOrgNotAllowCreate) ? 'disabled' : ''}`}>
                            <div className={`${className}__permission__icon edit`}>
                                <EditIcon />
                            </div>
                            <div className={`${className}__permission__content`}>
                                <div className={`${className}__permission__header`}>
                                    <div className={`${className}__permission__info`}>
                                        <h4 className={`${className}__permission__name`}>Create And Edit</h4>
                                        <p className={`${className}__permission__desc`}>
                                            Can create and edit items
                                        </p>
                                    </div>
                                    <ToggleStyleSwitch
                                        onChange={handleWritePermissionsCheck}
                                        checked={writePermission && readPermission}
                                        disabled={!readPermission || isOrgNotAllowCreate || loading}/>
                                </div>
                            </div>
                        </div>

                        {/* Admin Permission */}
                        <div className={`${className}__permission ${(!readPermission || !writePermission || props.childType === 'team') ? 'disabled' : ''}`}>
                            <div className={`${className}__permission__icon admin`}>
                                <AdminIcon />
                            </div>
                            <div className={`${className}__permission__content`}>
                                <div className={`${className}__permission__header`}>
                                    <div className={`${className}__permission__info`}>
                                        <h4 className={`${className}__permission__name`}>Admin Privileges</h4>
                                        <p className={`${className}__permission__desc`}>
                                            Can manage users and permissions {props.childType === 'team' ? '(Not available for teams)' : ''}
                                        </p>
                                    </div>
                                    <ToggleStyleSwitch
                                        onChange={handleAdminPermissionsCheck}
                                        checked={adminPermission && writePermission && readPermission}
                                        disabled={!writePermission || props.childType === 'team' || isOrgNotAllowCreate || loading}/>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className={`${className}__actions`}>
                        <button
                            className={`${className}__actions__cancel`}
                            onClick={props.closePopup}
                            disabled={loading}>
                            Cancel
                        </button>
                        <button
                            className={`${className}__actions__add`}
                            onClick={handleSendInvite}
                            disabled={loading}>
                            {loading ? (
                                <>
                                    <CircularProgress size={18} sx={{ color: '#fff' }} />
                                    <span>Adding...</span>
                                </>
                            ) : (
                                `Add ${props.childType === 'user' ? 'User' : 'Team'}`
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
